import { useEffect, useMemo, useState } from 'react'

import config from 'src/config'
import { post } from 'src/functions/fetch'
import { discount, tProductCouponPromotion, tProductPreview } from 'src/shared/frontendTypes/product'
import { priceLock } from 'src/shared/frontendTypes/warenkorb'
import {
  isProductCommercePreviews,
  tProductCommercePreview,
} from 'src/shared/frontendTypes/productCommerce'
import { productCommerceItem } from 'src/shared/apiRequests/productCommerce'
import { ProductPurchaseMode } from 'src/shared/productPurchaseMode'

type CommerceSource = {
  id: number
  key: string
  hash?: string
  price: number
  deliverytime: number
  stockDeliverytime?: number
  stock?: number
  shippingType?: number
  discount?: discount
  couponPromotion?: tProductCouponPromotion | null
  priceLock?: priceLock
  purchaseMode?: ProductPurchaseMode
  inquireOnly?: true
}

const MAX_PRODUCTS_PER_BATCH = 120
const REFRESH_INTERVAL_MS = 30000

const keyOf = ({ id, key }: productCommerceItem) => `${id}:${key}`

const cache = new Map<string, tProductCommercePreview>()
const listeners = new Map<string, Set<(product: tProductCommercePreview) => void>>()
const subscribedProducts = new Map<string, productCommerceItem>()
const queue = new Map<string, productCommerceItem>()

let batchTimer: number | null = null
let refreshTimer: number | null = null

const notify = (product: tProductCommercePreview) => {
  const key = keyOf(product)
  const entries = listeners.get(key)
  if (!entries?.size) return
  entries.forEach(listener => listener(product))
}

const flush = async () => {
  const products = Array.from(queue.values())
  queue.clear()

  if (products.length === 0) return

  batchTimer = null

  const chunks = Array.from(
    { length: Math.ceil(products.length / MAX_PRODUCTS_PER_BATCH) },
    (_, index) => products.slice(index * MAX_PRODUCTS_PER_BATCH, (index + 1) * MAX_PRODUCTS_PER_BATCH),
  )

  await Promise.all(chunks.map(async (chunk) => {
    try {
      const response = await post({
        url: 'product/previews/commerce',
        body: {
          ...config.shop,
          products: chunk,
        },
      })

      if (!isProductCommercePreviews(response)) return

      response.forEach(product => {
        cache.set(keyOf(product), product)
        notify(product)
      })
    } catch (_error) {
      // The static preview remains visible if live commerce data cannot be refreshed.
    }
  }))
}

const enqueue = (product: productCommerceItem) => {
  queue.set(keyOf(product), product)
  if (batchTimer !== null) return
  batchTimer = window.setTimeout(() => {
    void flush()
  }, 24)
}

const refreshSubscribedProducts = () => {
  subscribedProducts.forEach(product => enqueue(product))
}

const ensureRefreshTimer = () => {
  if (refreshTimer !== null || typeof window === 'undefined') return

  refreshTimer = window.setInterval(() => {
    if (document.visibilityState !== 'visible') return
    refreshSubscribedProducts()
  }, REFRESH_INTERVAL_MS)
}

const stopRefreshTimerIfIdle = () => {
  if (refreshTimer === null || subscribedProducts.size > 0 || typeof window === 'undefined') return

  window.clearInterval(refreshTimer)
  refreshTimer = null
}

const subscribe = (
  product: productCommerceItem,
  listener: (product: tProductCommercePreview) => void,
) => {
  const key = keyOf(product)

  if (!listeners.has(key)) listeners.set(key, new Set())
  listeners.get(key)!.add(listener)
  subscribedProducts.set(key, product)

  const cached = cache.get(key)
  if (cached) listener(cached)

  enqueue(product)
  ensureRefreshTimer()

  return () => {
    const entries = listeners.get(key)
    if (!entries) return
    entries.delete(listener)
    if (entries.size === 0) {
      listeners.delete(key)
      subscribedProducts.delete(key)
      stopRefreshTimerIfIdle()
    }
  }
}

const mergeDiscount = (
  sourceDiscount?: discount,
  liveDiscount?: discount,
): discount | undefined => {
  // A successful commerce response is authoritative: an omitted discount means
  // the promotion ended, rather than permission to retain a cached old price.
  if (!liveDiscount) return undefined

  if (
    !liveDiscount.quantityDiscount
    && sourceDiscount?.quantityDiscount
    && sourceDiscount.id === liveDiscount.id
  ) {
    return {
      ...liveDiscount,
      quantityDiscount: sourceDiscount.quantityDiscount,
    }
  }

  return liveDiscount
}

const mergeCommerce = <T extends CommerceSource>(source: T, response?: CommerceSource | null): T => {
  // Selection changes render before useEffect switches the subscription. Never
  // paint the previous variant's price/stock during that first render.
  const live = response?.id === source.id && response.key === source.key ? response : undefined
  return {
    ...source,
    hash: live?.hash ?? source.hash,
    price: live?.price ?? source.price,
    deliverytime: live?.deliverytime ?? source.deliverytime,
    stockDeliverytime: live ? live.stockDeliverytime : source.stockDeliverytime,
    stock: typeof live?.stock === 'number' ? live.stock : source.stock,
    shippingType: live?.shippingType ?? source.shippingType,
    discount: live ? mergeDiscount(source.discount, live.discount) : source.discount,
    couponPromotion: live && Object.prototype.hasOwnProperty.call(live, 'couponPromotion')
      ? live.couponPromotion ?? undefined
      : source.couponPromotion,
    priceLock: live ? live.priceLock : source.priceLock,
    purchaseMode: live ? live.purchaseMode ?? 'cart' : source.purchaseMode,
    inquireOnly: live ? live.inquireOnly : source.inquireOnly,
  }
}

export const useLiveProductCommerce = <T extends CommerceSource>(
  source: T | null | undefined,
): T | null => {
  const [liveProduct, setLiveProduct] = useState<tProductCommercePreview | null>(null)

  useEffect(() => {
    if (!source || typeof window === 'undefined') {
      setLiveProduct(null)
      return
    }

    setLiveProduct(cache.get(keyOf(source)) || null)

    const unsubscribe = subscribe(
      { id: source.id, key: source.key },
      (product) => setLiveProduct(product),
    )

    return () => {
      unsubscribe()
    }
  }, [source?.id, source?.key])

  return useMemo(() => {
    if (!source) return null
    return mergeCommerce(source, liveProduct)
  }, [liveProduct, source])
}

/** Keep the whole curated row subscribed, including temporarily sold-out cards. */
export const useLiveProductCommerceList = (
  sources: tProductPreview[],
  enabled = false,
): tProductPreview[] => {
  const [liveProducts, setLiveProducts] = useState<Map<string, tProductCommercePreview>>(() => new Map())
  const signature = sources.map(source => keyOf(source)).join('|')

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    const products = Array.from(new Map(sources.map(source => [keyOf(source), {
      id: source.id, key: source.key,
    }])).values())
    const cachedProducts = new Map(products.flatMap(product => {
      const cached = cache.get(keyOf(product))
      return cached ? [[keyOf(product), cached] as const] : []
    }))
    setLiveProducts(current => current.size === 0 && cachedProducts.size === 0 ? current : cachedProducts)
    const unsubscribe = products.map(product => subscribe(product, next => {
      setLiveProducts(current => new Map(current).set(keyOf(next), next))
    }))
    return () => unsubscribe.forEach(stop => stop())
    // Identity changes drive subscriptions; current source values merge below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, signature])

  return useMemo(() => enabled
    ? sources.map(source => mergeLivePreviewCommerce(source, liveProducts.get(keyOf(source))))
    : sources, [enabled, sources, liveProducts])
}

export const mergeLivePreviewCommerce = (
  preview: tProductPreview,
  response: CommerceSource | null | undefined,
): tProductPreview => mergeCommerce(preview, response)
